import { Link } from "@/components/ui/link";
import { withReturnTo } from "@/lib/return-to";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button, Label } from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";
import { attachKb } from "@/lib/api/endpoints";
import { useKnowledgeBases } from "@/lib/query/hooks";
import { qk } from "@/lib/query/keys";

const selectClass =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

/** Attach an existing knowledge base to a version. Already-attached slugs hidden. */
export function AttachKbDialog({
  open,
  onOpenChange,
  agentSlug,
  label,
  attachedSlugs,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agentSlug: string;
  label: string;
  attachedSlugs: string[];
}) {
  const qc = useQueryClient();
  const kbs = useKnowledgeBases();
  const available = (kbs.data ?? []).filter((kb) => !attachedSlugs.includes(kb.slug));

  const [slug, setSlug] = React.useState("");

  React.useEffect(() => {
    if (open) setSlug(available[0]?.slug ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, kbs.data]);

  const mutation = useMutation({
    mutationFn: () => attachKb(agentSlug, label, slug),
    onSuccess: async () => {
      toast.success("Knowledge base attached.");
      await qc.invalidateQueries({ queryKey: qk.attachedKbs(agentSlug, label) });
      await qc.invalidateQueries({ queryKey: qk.knowledgeBases });
      onOpenChange(false);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Attach knowledge base</DialogTitle>
          <DialogDescription>
            Give this version grounded access to one of your knowledge bases.
          </DialogDescription>
        </DialogHeader>
        {available.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No unattached knowledge bases available.{" "}
            <Link
              href={withReturnTo("/knowledge-bases", `/agents/${agentSlug}/versions/${label}`)}
              className="underline underline-offset-4"
            >
              Register one
            </Link>{" "}
            first — you will be brought back here.
          </p>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (slug) mutation.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="ak-kb">Knowledge base</Label>
              <select
                id="ak-kb"
                className={selectClass}
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
              >
                {available.map((kb) => (
                  <option key={kb.slug} value={kb.slug}>
                    {kb.name} ({kb.status})
                  </option>
                ))}
              </select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={!slug || mutation.isPending}>
                {mutation.isPending ? "Attaching…" : "Attach"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
